import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../../services/AuthService';
import logo from '../../../assets/logo.png';

const ACCENT = '#005e5d';
const BORDER = '#E6E7EB';
const TEXT_PRIMARY = '#333333';
const TEXT_SECONDARY = '#666666';
const TEXT_TERTIARY = '#999999';
const DESTRUCTIVE = '#D32F2F';

/**
 * Admin Login Page
 * Provides authentication interface with consistent admin dashboard design
 */
export const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!email.trim()) {
      setError('Vui lòng nhập email');
      return;
    }

    if (!email.includes('@')) {
      setError('Email không hợp lệ');
      return;
    }

    if (!password) {
      setError('Vui lòng nhập mật khẩu');
      return;
    }

    if (password.length < 6) {
      setError('Mật khẩu phải có ít nhất 6 ký tự');
      return;
    }

    try {
      setLoading(true);
      const response = await AuthService.login({ email, password });

      if (response.statusCode === 200 && response.data) {
        // Save remember me preference
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }

        // Check if user needs to reset password
        if (response.data.needsPasswordReset) {
          setSuccess('Đăng nhập thành công! Vui lòng đặt lại mật khẩu...');
          setTimeout(() => {
            navigate('/admin/reset-password', { replace: true });
          }, 1000);
        } else {
          setSuccess('Đăng nhập thành công! Đang chuyển hướng...');
          setTimeout(() => {
            navigate('/admin', { replace: true });
          }, 1000);
        }
      } else {
        setError(response.error || 'Đăng nhập thất bại');
      }
    } catch (err) {
      setError('Lỗi kết nối. Vui lòng thử lại');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F2F3F5',
        backgroundImage: `
          linear-gradient(135deg, rgba(0, 94, 93, 0.05) 0%, rgba(1, 126, 110, 0.05) 100%)
        `,
        padding: '20px',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={2}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: '16px',
            border: `1px solid ${BORDER}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                width: 72,
                height: 72,
                borderRadius: '14px',
                objectFit: 'contain',
                mx: 'auto',
                mb: 2,
                display: 'block',
              }}
            />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: TEXT_PRIMARY,
                mb: 0.5,
                letterSpacing: '-0.01em',
              }}
            >
              Admin Manager
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: TEXT_SECONDARY,
              }}
            >
              Nhập thông tin đăng nhập để truy cập bảng điều khiển
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5,
                borderRadius: '8px',
                backgroundColor: '#FFEBEE',
                color: DESTRUCTIVE,
                '& .MuiAlert-icon': {
                  color: DESTRUCTIVE,
                },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 2.5,
                borderRadius: '8px',
              }}
            >
              {success}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleLogin} noValidate>
            {/* Email Field */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: TEXT_PRIMARY, mb: 0.75, fontSize: '13px' }}
              >
                Email
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder="Nhập email của bạn"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1.5 },
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: TEXT_TERTIARY, opacity: 0.7 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: TEXT_TERTIARY, mr: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Password Field */}
            <Box sx={{ mb: 2.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: TEXT_PRIMARY, mb: 0.75, fontSize: '13px' }}
              >
                Mật Khẩu
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                placeholder="Nhập mật khẩu của bạn"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    '&:hover fieldset': { borderColor: ACCENT },
                    '&.Mui-focused fieldset': { borderColor: ACCENT, borderWidth: 1.5 },
                  },
                  '& .MuiOutlinedInput-input::placeholder': { color: TEXT_TERTIARY, opacity: 0.7 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: TEXT_TERTIARY, mr: 1 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={loading}
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon sx={{ color: TEXT_TERTIARY }} />
                        ) : (
                          <VisibilityIcon sx={{ color: TEXT_TERTIARY }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Remember Me Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                  sx={{
                    color: TEXT_TERTIARY,
                    '&.Mui-checked': {
                      color: ACCENT,
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                  Ghi nhớ tôi
                </Typography>
              }
              sx={{
                mb: 3,
              }}
            />

            {/* Login Button */}
            <Button
              fullWidth
              type="submit"
              disabled={loading}
              sx={{
                height: '44px',
                borderRadius: '10px',
                backgroundColor: ACCENT,
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '15px',
                textTransform: 'none',
                transition: 'all 0.3s ease',
                mb: 2,
                '&:hover:not(:disabled)': {
                  backgroundColor: '#004d4c',
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 16px ${ACCENT}30`,
                },
                '&:active:not(:disabled)': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  backgroundColor: BORDER,
                  color: TEXT_TERTIARY,
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'inherit' }} />
              ) : (
                'Đăng Nhập'
              )}
            </Button>

            {/* Info Text */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                color: TEXT_TERTIARY,
                fontStyle: 'italic',
              }}
            >
              Này là trang đăng nhập dành cho quản trị viên
            </Typography>
          </Box>
        </Paper>

        {/* Footer Info */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="caption" sx={{ color: TEXT_TERTIARY }}>
            Phiên bản 1.0 | © 2026 AnhNguJennifer Admin
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default AdminLogin;
